import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectFirestoreEmulator, Firestore, getFirestore, initializeFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectFunctionsEmulator, Functions, getFunctions, provideFunctions } from '@angular/fire/functions';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';
import { MegaMenuModule } from 'primeng/megamenu';
import { sagaOrangePrimeNGTheme } from '../theme/saga-orange-prime';
import {
	FIREBASE_EMULATOR_HOST,
	FIREBASE_EMULATOR_PORTS,
	FIRESTORE_DATABASE_ID,
	FIRESTORE_EMULATOR_DATABASE_ID,
} from '@firebase-client-config';
import { getEmulatorAuthUrl, purgeStaleEmulatorAuthSession, shouldUseFirebaseEmulators } from '../../../../firebase-emulator-auth';
import { firebase, useFirebaseEmulators } from 'src/environments/environment';
import { AppComponent } from '../app.component';
import { DashboardComponent } from '../components/dashboard/dashboard.component';
import { NavbarComponent } from '../components/navbar/navbar.component';
import { ParentsComponent } from '../components/parents/parents.component';
import { SignInComponent } from '../components/sign-in/sign-in.component';
import { YASComponent } from '../components/yas/yas.component';
import { AuthGuard } from '../guards/auth.guard';
import { AppRoutingModule } from '../routing/app.routing';
import { GoogleIdentityService } from '../services/identity.service';
import { NGPrimeModule } from './ngprime.module';

const useEmulators = shouldUseFirebaseEmulators(useFirebaseEmulators);

@NgModule({
	declarations: [AppComponent, DashboardComponent, NavbarComponent, ParentsComponent, SignInComponent, YASComponent],
	imports: [
		AppRoutingModule,
		BrowserAnimationsModule,
		BrowserModule,
		FormsModule,
		HttpClientModule,
		ReactiveFormsModule,
		NGPrimeModule,
		MegaMenuModule,
	],
	providers: [
		providePrimeNG({
			theme: sagaOrangePrimeNGTheme,
		}),
		provideFirebaseApp(() => initializeApp(firebase.Cfg)),
		provideAuth(() => {
			const auth = getAuth();
			if (useEmulators) {
				connectAuthEmulator(auth, getEmulatorAuthUrl(), { disableWarnings: true });
				void purgeStaleEmulatorAuthSession(auth);
			}
			return auth;
		}),
		provideFirestore(() => {
			const app = getApp();
			let firestore: Firestore;
			if (useEmulators) {
				const emulatorDbId = FIRESTORE_EMULATOR_DATABASE_ID;
				firestore = initializeFirestore(app, { experimentalForceLongPolling: true }, emulatorDbId);
				connectFirestoreEmulator(firestore, FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS.firestore);
			} else {
				firestore = getFirestore(app, FIRESTORE_DATABASE_ID);
			}
			return firestore;
		}),
		provideFunctions(() => {
			let functions: Functions;
			if (useEmulators) {
				functions = getFunctions();
				connectFunctionsEmulator(functions, FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS.functions);
			} else {
				functions = getFunctions();
			}
			return functions;
		}),
		provideStorage(() => {
			const storage = getStorage();
			if (useEmulators) {
				connectStorageEmulator(storage, FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS.storage);
			}
			return storage;
		}),
		GoogleIdentityService,
		AuthGuard,
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler(),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
